/**
 * Referrer drill-down at /admin/v2/friends/:user_id.
 *
 * Renders the referrer's identity + the list of every user they've
 * referred (paginated). Backed by GET /admin/metrics/referrers/{id}/referred.
 * Used by the "Top referrers" panel on the Friends screen — click a
 * row there and land here.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  errorState,
  escape,
  fmtInt,
  fmtMonthYear,
  fmtRelTime,
  mountSortableTable,
  skeletonGrid,
} from './common.js';

export async function renderReferrerDetail(host, ctx) {
  if (!ctx.referrerId) {
    host.innerHTML = errorState('Missing referrer id.');
    return;
  }
  host.innerHTML = skeletonGrid(3);
  try {
    const data = await api(
      `/admin/metrics/referrers/${encodeURIComponent(ctx.referrerId)}/referred?limit=200`,
    );
    host.innerHTML = _render(data, ctx);
    _mountReferredTable(host, data);
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        ctx.navigate('/login');
        return;
      }
      if (err.status === 404) {
        host.innerHTML = `<div class="adm2-error">User not found.</div>
          <a href="/admin/v2/friends" data-link class="adm2-btn" style="margin-top:12px;display:inline-block;">← Back to Friends</a>`;
        return;
      }
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

function _render(data, ctx) {
  const r = data.referrer || {};
  const items = data.items || [];
  const rewarded = items.filter((i) => i.status === 'rewarded').length;
  const joined = items.length - rewarded;
  const name = r.username || (r.email || '').split('@')[0] || 'user';
  const initial = (name.charAt(0) || '?').toUpperCase();

  return `
    <a href="/admin/v2/friends" data-link class="adm2-btn" style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">‹ Top referrers</a>

    <div class="adm2-hero">
      <div style="position:absolute;right:-50px;top:-70px;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(255,255,255,.20),transparent 65%);"></div>
      <div class="adm2-hero-row">
        <div class="adm2-hero-avatar">${escape(initial)}</div>
        <div style="flex:1;min-width:220px;">
          <div class="adm2-hero-name">${escape(name)}</div>
          <div class="adm2-hero-sub">
            <span style="font-family:'IBM Plex Mono',monospace;">${escape(r.email || '')}</span>
            ${r.referral_code ? `<span class="adm2-hero-badge">CODE ${escape(r.referral_code)}</span>` : ''}
            ${r.is_admin ? '<span class="adm2-hero-badge">ADMIN</span>' : ''}
          </div>
        </div>
        <a href="/admin/v2/players/${escape(r.user_id || '')}" data-link class="adm2-hero-btn" style="text-decoration:none;">Open profile</a>
      </div>
    </div>

    <div class="adm2-section-label">Referrals</div>
    <div class="adm2-grid" style="grid-template-columns:repeat(auto-fit,minmax(180px,1fr));">
      ${_smallStat('Total referred', fmtInt(data.total))}
      ${_smallStat('Rewarded', fmtInt(rewarded))}
      ${_smallStat('Joined only', fmtInt(joined))}
    </div>

    <div class="adm2-panel" style="margin-top:18px;padding:0;overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid var(--a-border);">
        <div class="adm2-panel-title">Referred users</div>
        <div class="adm2-panel-sub">every user this referrer has invited · newest first · click a row to open their profile</div>
      </div>
      <div id="adm2-referred-list" style="padding:8px 4px;"></div>
    </div>
  `;
  // silence lint for ctx being unused here — we lean on it in the mount fn.
  // eslint-disable-next-line no-unused-vars
  ctx;
}

function _mountReferredTable(host, data) {
  const target = host.querySelector('#adm2-referred-list');
  if (!target) return;
  const items = data.items || [];
  if (!items.length) {
    target.innerHTML =
      '<div style="padding:32px 16px;text-align:center;color:var(--a-faint);">This user hasn\'t referred anyone yet.</div>';
    return;
  }
  mountSortableTable(target, {
    columns: [
      {
        key: 'user',
        label: 'User',
        text: true,
        get: (r) => (r.email || '').toLowerCase(),
        cell: (r) => `
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <a class="link" data-open-user="${escape(r.user_id || '', true)}">${escape(r.username || (r.email || '').split('@')[0] || 'user')}</a>
            ${!r.email_verified ? '<span class="adm2-chip w">UNVERIFIED</span>' : ''}
          </div>
          <div style="font:400 11px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-top:3px;">${escape(r.email || '')}</div>
        `,
      },
      {
        key: 'status',
        label: 'Status',
        text: true,
        get: (r) => r.status || '',
        cell: (r) =>
          r.status === 'rewarded'
            ? '<span class="adm2-chip g">REWARDED</span>'
            : '<span class="adm2-chip">JOINED</span>',
      },
      {
        key: 'referred_at',
        label: 'Referred',
        right: true,
        get: (r) => (r.referred_at ? new Date(r.referred_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.referred_at)),
      },
      {
        key: 'rewarded_at',
        label: 'Rewarded',
        right: true,
        get: (r) => (r.rewarded_at ? new Date(r.rewarded_at).getTime() : 0),
        cell: (r) =>
          r.rewarded_at
            ? `<span style="color:var(--a-ok);">${escape(fmtRelTime(r.rewarded_at))}</span>`
            : '<span style="color:var(--a-faint);">—</span>',
      },
      {
        key: 'user_created_at',
        label: 'Signed up',
        right: true,
        get: (r) => (r.user_created_at ? new Date(r.user_created_at).getTime() : 0),
        cell: (r) => escape(fmtMonthYear(r.user_created_at)),
      },
      {
        key: 'user_last_seen_at',
        label: 'Last seen',
        right: true,
        get: (r) => (r.user_last_seen_at ? new Date(r.user_last_seen_at).getTime() : 0),
        cell: (r) => escape(fmtRelTime(r.user_last_seen_at)),
      },
    ],
    rows: items,
    defaultSort: { key: 'referred_at', dir: 'desc' },
    minWidth: 900,
    pageSizeKey: 'referrer.referred',
    emptyMessage: 'No referred users.',
  });
  // Row click → open the referred user's profile.
  target.querySelectorAll('[data-open-user]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const uid = el.dataset.openUser;
      if (uid) window.location.href = `/admin/v2/players/${uid}`;
    });
  });
}

function _smallStat(label, value) {
  return `
    <div style="background:var(--a-panel);border:1px solid var(--a-border);border-radius:14px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.05);">
      <div style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-muted);">${escape(label)}</div>
      <div style="font:600 28px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:11px;">${escape(value)}</div>
    </div>
  `;
}
